import { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { useFrontendAuth } from '../hooks/useAuth';
import { AxiosError } from 'axios';
import { useTranslation } from '../context/TranslationContext';
import { scrollToFirstErrorField } from '../utils/scrollToFirstError';
import { ValidationError } from '../admin/components/ValidationError';
import { shopAxios } from '../services/api.shop';
import { useDocumentTitle } from '../hooks/useDocumentTitle';
import { translateWithFallback } from '../utils/translateWithFallback';
import AnimatedPetitoLogo from '../components/AnimatedPetitoLogo';
import LoadingSplash from '../components/LoadingSplash';
import GoogleSignInButton from '../components/GoogleSignInButton';

type FieldErrors = Record<string, string>;

const baseInputClass =
    'w-full min-h-11 rounded-xl border bg-[color-mix(in_srgb,var(--bg-color),white_30%)] p-3 text-base text-[var(--text-color)] placeholder:text-[color-mix(in_srgb,var(--text-color),transparent_50%)] outline-none transition focus:border-[var(--highlight-color,#5A8A62)] focus:ring-2 focus:ring-[var(--highlight-color,#5A8A62)]/30';

/** Safe same-origin relative path only (blocks open redirects). */
function resolvePostLoginPath(
    returnUrl: string | null,
    stateFrom: unknown
): string {
    const candidates = [returnUrl, typeof stateFrom === 'string' ? stateFrom : null];
    for (const raw of candidates) {
        if (!raw || !raw.startsWith('/') || raw.startsWith('//')) continue;
        if (raw.startsWith('/login') || raw.startsWith('/register')) continue;
        return raw;
    }
    return '/home';
}

const Login = () => {
    const { user, loading, login, loginWithGoogle } = useFrontendAuth();
    const navigate = useNavigate();
    const location = useLocation();
    const [searchParams] = useSearchParams();
    const [email, setEmail] = useState('');
    const [fieldErrors, setFieldErrors] = useState<FieldErrors>({});
    const [password, setPassword] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [needsConfirmation, setNeedsConfirmation] = useState(false);
    const [resendMessage, setResendMessage] = useState('');
    const [resending, setResending] = useState(false);
    const { t, currentLang } = useTranslation();
    const tr = (key: string, fb: string) => translateWithFallback(t, key, fb);
    useDocumentTitle(tr('page.login.title', 'Log in'));

    const postLoginPath = resolvePostLoginPath(
        searchParams.get('returnUrl'),
        (location.state as { from?: string } | null)?.from
    );

    useEffect(() => {
        if (!loading && user) {
            navigate(postLoginPath, { replace: true });
        }
    }, [loading, user, navigate, postLoginPath]);

    const handleResend = async () => {
        if (!email.trim()) return;
        setResending(true);
        setResendMessage('');
        try {
            await shopAxios.post('/auth/resend-confirmation', { email: email.trim() });
            setResendMessage(t('page.login.resendSent'));
        } catch (err: unknown) {
            const status = (err as { response?: { status?: number } })?.response?.status;
            setResendMessage(
                status === 429 ? t('error.toomanyattempts') : t('page.login.resendFailed')
            );
        } finally {
            setResending(false);
        }
    };

    const clearFieldError = (field: string) => {
        setFieldErrors((prev) => {
            if (!prev[field] && !prev.General) return prev;
            const next = { ...prev };
            delete next[field];
            delete next.General;
            return next;
        });
    };

    const inputClass = (field: string) =>
        `${baseInputClass} ${
            fieldErrors[field] ? 'border-red-300 bg-red-50/30' : 'border-[color-mix(in_srgb,var(--accent-color),transparent_50%)]'
        }`;

    const validateClient = (): FieldErrors => {
        const errors: FieldErrors = {};
        if (!email.trim()) {
            errors.email = tr('partner.form.errors.emailRequired', 'Email is required.');
        } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())) {
            errors.email = tr('partner.form.errors.emailInvalid', 'Enter a valid email address.');
        }
        if (!password) {
            errors.password = tr('form.login.errors.passwordRequired', 'Password is required.');
        }
        return errors;
    };

    const mapAuthError = (err: unknown): FieldErrors => {
        if (err instanceof AxiosError) {
            if (err.response?.status === 429) {
                return { General: t('error.toomanyattempts') };
            }
            if (err.response?.status === 401) {
                if (err.response.data?.error === 'email_not_confirmed') {
                    setNeedsConfirmation(true);
                    return { email: t('page.login.emailNotConfirmed') };
                }
                if (err.response.data?.error === 'account_blocked') {
                    navigate('/account-status', {
                        state: { status: err.response.data.status, message: err.response.data.message },
                    });
                    return {};
                }
                if (err.response.data?.error === 'google_auth_failed') {
                    return { General: tr('error.googleSignIn', 'Google sign-in failed. Please try again.') };
                }
                return { password: t('error.invalidcredentials') };
            }
            return { General: t('error.unexpected') };
        }
        return { General: t('error.unexpected') };
    };

    const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        setNeedsConfirmation(false);
        setResendMessage('');
        const clientErrors = validateClient();
        if (Object.keys(clientErrors).length > 0) {
            setFieldErrors(clientErrors);
            scrollToFirstErrorField(clientErrors);
            return;
        }
        setFieldErrors({});
        setIsSubmitting(true);
        try {
            await login(email, password);
            navigate(postLoginPath, { replace: true });
        } catch (err: unknown) {
            const next = mapAuthError(err);
            setFieldErrors(next);
            scrollToFirstErrorField(next);
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleGoogle = async (idToken: string) => {
        setIsSubmitting(true);
        setNeedsConfirmation(false);
        setResendMessage('');
        setFieldErrors({});
        try {
            await loginWithGoogle(idToken, currentLang);
            navigate(postLoginPath, { replace: true });
        } catch (err: unknown) {
            const next = mapAuthError(err);
            setFieldErrors(next);
            scrollToFirstErrorField(next);
        } finally {
            setIsSubmitting(false);
        }
    };

    if (loading && !isSubmitting) {
        return (
            <LoadingSplash
                fullScreen={false}
                label={tr('state.loading', 'Loading')}
                className="min-h-[50dvh] px-4"
            />
        );
    }

    return (
        <div className="flex min-h-[60dvh] items-center justify-center py-8">
            <form
                noValidate
                onSubmit={handleSubmit}
                className="w-full max-w-md rounded-2xl border border-[color-mix(in_srgb,var(--accent-color),transparent_55%)] bg-[color-mix(in_srgb,var(--bg-color),white_25%)] p-6 shadow-lg sm:p-8"
            >
                <div className="mb-6 flex flex-col items-center text-center">
                    <AnimatedPetitoLogo size="md" mode="css" playful className="mb-4" />
                    <h2 className="text-2xl font-bold text-[var(--text-color)]">
                        {t('page.login.title')}
                    </h2>
                    <p className="mt-2 text-sm text-[color-mix(in_srgb,var(--text-color),transparent_35%)]">
                        {tr(
                            'auth.tagline',
                            "Belgium's marketplace for pre-loved baby gear"
                        )}
                    </p>
                </div>
                <div className="mb-4">
                    <input
                        type="email"
                        name="email"
                        placeholder={t('form.login.email')}
                        value={email}
                        onChange={(e) => {
                            setEmail(e.target.value);
                            clearFieldError('email');
                        }}
                        className={inputClass('email')}
                        autoComplete="email"
                        disabled={isSubmitting}
                    />
                    <ValidationError error={fieldErrors.email} />
                </div>
                <div className="mb-2">
                    <input
                        type="password"
                        name="password"
                        placeholder={t('form.login.password')}
                        value={password}
                        onChange={(e) => {
                            setPassword(e.target.value);
                            clearFieldError('password');
                        }}
                        className={inputClass('password')}
                        autoComplete="current-password"
                        disabled={isSubmitting}
                    />
                    <ValidationError error={fieldErrors.password} />
                </div>
                <p className="mb-4 text-right text-sm">
                    <Link
                        to="/forgot-password"
                        className="font-medium text-[var(--highlight-color,#5A8A62)] underline underline-offset-2"
                    >
                        {t('page.login.forgotPassword')}
                    </Link>
                </p>
                <ValidationError error={fieldErrors.General} />
                {needsConfirmation && (
                    <div className="mb-4 space-y-2 rounded-xl border border-amber-200/80 bg-amber-50/90 p-3 text-sm text-amber-900">
                        <p>{t('page.login.confirmHint')}</p>
                        <button
                            type="button"
                            onClick={handleResend}
                            disabled={resending}
                            className="font-medium underline disabled:opacity-60"
                        >
                            {resending
                                ? t('state.loading')
                                : t('page.login.resendConfirmation')}
                        </button>
                        {resendMessage && <p className="text-green-800">{resendMessage}</p>}
                        <p>
                            <Link to="/check-email" state={{ email }} className="underline">
                                {t('page.login.checkEmailPage')}
                            </Link>
                        </p>
                    </div>
                )}
                <button
                    type="submit"
                    disabled={isSubmitting}
                    className={`flex w-full items-center justify-center rounded-full px-4 py-3 font-semibold transition-colors ${
                        isSubmitting
                            ? 'cursor-not-allowed bg-[var(--accent-color)]/70 text-white/70'
                            : 'bg-[var(--accent-color)] text-white hover:bg-[color-mix(in_srgb,var(--accent-color),black_12%)]'
                    }`}
                >
                    {isSubmitting ? (
                        <div className="flex items-center justify-center">
                            <div className="mr-3 h-5 w-5 animate-spin rounded-full border-t-2 border-b-2 border-[var(--text-color)]"></div>
                            {t('state.loggingin')}
                        </div>
                    ) : (
                        t('form.login.submit')
                    )}
                </button>

                <GoogleSignInButton
                    onCredential={handleGoogle}
                    onError={(msg) => setFieldErrors({ General: msg })}
                    disabled={isSubmitting}
                />

                <p className="mt-6 text-center text-sm text-[color-mix(in_srgb,var(--text-color),transparent_30%)]">
                    {t('page.login.noAccount')}{' '}
                    <Link
                        to="/register"
                        className="font-semibold text-[var(--highlight-color,#5A8A62)] hover:underline"
                    >
                        {t('page.login.signUp')}
                    </Link>
                </p>
            </form>
        </div>
    );
};

export default Login;
